// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useState } from 'react';
import { Image, Platform, ScrollView, StyleSheet, View, useWindowDimensions } from 'react-native';
import { useGlobalSearchParams, useRouter } from 'expo-router';

import { Text } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import { useI18n } from '@/i18n';
import { getAssetImageSource } from '@/services/assetImageSource';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import appColors from '@/theme/colors';
import { useUser } from '@/app/user-context';

type TourSummary = {
  id: string;
  status?: string;
  Status?: string;
  isHidden?: boolean;
  IsHidden?: boolean;
  unfinishedRoundCount?: number;
  UnfinishedRoundCount?: number;
};

type TourRoundSummary = {
  status?: string;
  Status?: string;
};

export default function WelcomeScreen() {
  const router = useRouter();
  const { t } = useI18n();
  const { user } = useUser();
  const { width, height } = useWindowDimensions();
  const { debug: debugParam } = useGlobalSearchParams<{ debug?: string }>();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const debug = debugParam === 'true';
  const debugSuffix = debug ? '&debug=true' : '';
  const [openTourCount, setOpenTourCount] = useState(0);
  const [singleRoundCount, setSingleRoundCount] = useState(0);
  const compactLayout = height <= 860 || width <= 430;
  const extraCompactLayout = height <= 760;
  const startTourPath = Platform.OS === 'web'
    ? (debug ? '/tournaments?startTour=true&debug=true' : '/tournaments?startTour=true')
    : '/tournaments';
  const myToursPath = debug ? '/my-tours?debug=true' : '/my-tours';
  const loginHeroImage = useMemo(
    () => getAssetImageSource('images/login-hero.png', '', '/welcome', { width: 720 }),
    []
  );
  const myToursLabel = useMemo(() => {
    return t('welcome.myToursOpenSummary', {
      tours: openTourCount,
      singleRounds: singleRoundCount,
    });
  }, [openTourCount, singleRoundCount, t]);

  useEffect(() => {
    let cancelled = false;

    const loadCounts = async () => {
      if (!user?.token || !user.id) {
        if (!cancelled) {
          setOpenTourCount(0);
          setSingleRoundCount(0);
        }
        return;
      }

      try {
        const playerResponse = await fetch(`${apiBaseUrl}/api/player/by-user/${user.id}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!playerResponse.ok) {
          throw new Error(`Player lookup failed (${playerResponse.status})`);
        }
        const playerPayload = (await playerResponse.json()) as { id?: string };
        const playerId = playerPayload.id ?? '';
        if (!playerId) {
          throw new Error('Missing player id');
        }

        const toursResponse = await fetch(`${apiBaseUrl}/api/tours?playerId=${encodeURIComponent(playerId)}&includeHidden=true`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!toursResponse.ok) {
          throw new Error(`Tours lookup failed (${toursResponse.status})`);
        }
        const tours = (await toursResponse.json()) as TourSummary[];

        const isFinishedOrCancelled = (statusRaw: string | undefined) => {
          const status = (statusRaw ?? '').toLowerCase();
          return status === 'finished' || status === 'cancelled';
        };
        const isArchived = (statusRaw: string | undefined) => {
          return (statusRaw ?? '').toLowerCase() === 'archived';
        };

        const openTours = tours.filter((tour) => {
          const hidden = tour.isHidden ?? tour.IsHidden ?? false;
          const status = tour.status ?? tour.Status;
          return !hidden && !isArchived(status) && !isFinishedOrCancelled(status);
        });

        const hiddenTours = tours.filter((tour) => tour.isHidden ?? tour.IsHidden ?? false);
        const hiddenToursMissingRoundCounts = hiddenTours.filter(
          (tour) => tour.unfinishedRoundCount === undefined && tour.UnfinishedRoundCount === undefined
        );

        let unfinishedSingleRounds = hiddenTours.reduce(
          (total, tour) => total + (tour.unfinishedRoundCount ?? tour.UnfinishedRoundCount ?? 0),
          0
        );

        if (hiddenToursMissingRoundCounts.length > 0) {
          const hiddenTourRounds = await Promise.all(
            hiddenToursMissingRoundCounts.map(async (tour) => {
              try {
                const roundsResponse = await fetch(`${apiBaseUrl}/api/rounds/tour/${tour.id}`, {
                  headers: { Authorization: `Bearer ${user.token}` },
                });
                if (!roundsResponse.ok) {
                  return [] as TourRoundSummary[];
                }
                return (await roundsResponse.json()) as TourRoundSummary[];
              } catch {
                return [] as TourRoundSummary[];
              }
            })
          );

          unfinishedSingleRounds += hiddenTourRounds.reduce((total, rounds) => {
            const unfinished = rounds.filter((round) => !isFinishedOrCancelled(round.status ?? round.Status));
            return total + unfinished.length;
          }, 0);
        }

        if (!cancelled) {
          setOpenTourCount(openTours.length);
          setSingleRoundCount(unfinishedSingleRounds);
        }
      } catch {
        if (!cancelled) {
          setOpenTourCount(0);
          setSingleRoundCount(0);
        }
      }
    };

    void loadCounts();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, user?.id, user?.token]);

  return (
    <ScrollView contentContainerStyle={[styles.container, compactLayout && styles.containerCompact, extraCompactLayout && styles.containerExtraCompact]}>
      <Image source={loginHeroImage} style={[styles.hero, compactLayout && styles.heroCompact, extraCompactLayout && styles.heroExtraCompact]} resizeMode="contain" />
      <Text style={[styles.text, compactLayout && styles.textCompact, extraCompactLayout && styles.textExtraCompact]}>{t('welcome.body')}</Text>

      <View style={[styles.buttons, compactLayout && styles.buttonsCompact, extraCompactLayout && styles.buttonsExtraCompact]}>
        <GoffyButton
          label={t('welcome.startSingleRound')}
          onPress={() => router.push(`/tour/single-round/round-init?singleRound=true${debugSuffix}`)}
          tone="accent"
          style={[styles.ctaButton, compactLayout && styles.ctaButtonCompact, extraCompactLayout && styles.ctaButtonExtraCompact]}
          textStyle={[styles.ctaButtonText, compactLayout && styles.ctaButtonTextCompact]}
        />
        <GoffyButton
          label={t('welcome.startTour')}
          onPress={() => router.push(startTourPath)}
          tone="accent"
          style={[styles.ctaButton, compactLayout && styles.ctaButtonCompact, extraCompactLayout && styles.ctaButtonExtraCompact]}
          textStyle={[styles.ctaButtonText, compactLayout && styles.ctaButtonTextCompact]}
        />
        <View style={[styles.myToursButtonWrap, styles.ctaButton, compactLayout && styles.ctaButtonCompact, extraCompactLayout && styles.ctaButtonExtraCompact]}>
          <GoffyButton
            label={t('welcome.myTours')}
            onPress={() => router.push(myToursPath)}
            tone="accent"
            style={styles.myToursButtonFill}
            textStyle={[styles.ctaButtonText, compactLayout && styles.ctaButtonTextCompact]}
          />
          <Text style={styles.myToursMetaText}>{myToursLabel}</Text>
        </View>
      </View>
    </ScrollView>
  );
}

const cardShadow = Platform.select({
  web: { boxShadow: '0px 6px 18px rgba(0,0,0,0.3)' },
  default: {
    shadowColor: '#000000',
    shadowOpacity: 0.28,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 5 },
  },
});

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingHorizontal: 20,
    paddingTop: 44,
    paddingBottom: 30,
    backgroundColor: appColors.background.app,
    gap: 14,
  },
  containerCompact: {
    paddingTop: 16,
    paddingBottom: 18,
    gap: 10,
  },
  containerExtraCompact: {
    paddingTop: 10,
    gap: 8,
  },
  hero: {
    width: 420,
    maxWidth: '100%',
    height: 420,
    marginBottom: 0,
  },
  heroCompact: {
    width: 290,
    height: 290,
  },
  heroExtraCompact: {
    width: 240,
    height: 240,
  },
  title: {
    color: appColors.text.heading,
    fontSize: 62,
    fontWeight: '800',
    textAlign: 'center',
    lineHeight: 64,
  },
  titleCompact: {
    fontSize: 46,
    lineHeight: 48,
  },
  titleExtraCompact: {
    fontSize: 40,
    lineHeight: 42,
  },
  text: {
    width: '100%',
    maxWidth: 740,
    textAlign: 'center',
    color: appColors.text.secondary,
    fontSize: 16,
    lineHeight: 24,
    paddingVertical: 34,
    paddingHorizontal: 28,
    borderRadius: 22,
    backgroundColor: 'rgba(3, 42, 19, 0.72)',
    borderWidth: 1,
    borderColor: 'rgba(77, 154, 95, 0.35)',
    ...cardShadow,
  },
  textCompact: {
    fontSize: 15,
    lineHeight: 22,
    paddingVertical: 18,
    paddingHorizontal: 18,
    borderRadius: 16,
  },
  textExtraCompact: {
    fontSize: 14,
    lineHeight: 20,
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  buttons: {
    marginTop: 10,
    width: '100%',
    maxWidth: 360,
    alignItems: 'center',
    gap: 14,
  },
  buttonsCompact: {
    marginTop: 4,
    gap: 10,
  },
  buttonsExtraCompact: {
    marginTop: 2,
    gap: 8,
  },
  ctaButton: {
    width: 300,
    height: 74,
  },
  ctaButtonCompact: {
    width: 280,
    height: 62,
  },
  ctaButtonExtraCompact: {
    width: 268,
    height: 58,
  },
  ctaButtonText: {
    fontSize: 18,
    fontWeight: '800',
    letterSpacing: 2.1,
  },
  ctaButtonTextCompact: {
    fontSize: 16,
    letterSpacing: 1.4,
  },
  myToursButtonWrap: {
    position: 'relative',
    justifyContent: 'center',
    alignItems: 'center',
  },
  myToursButtonFill: {
    width: '100%',
    height: '100%',
  },
  myToursMetaText: {
    color: appColors.background.app,
    fontSize: 11,
    lineHeight: 13,
    letterSpacing: 0.1,
    fontWeight: '400',
    textTransform: 'none',
    textAlign: 'center',
    position: 'absolute',
    bottom: 8,
  },
});
